import { QueryClient } from '@tanstack/react-query';
import { globalProperties } from './global';
import { ObjectType } from '../Types/base';
import { MediabankImageSize } from '../Types/mediabank';

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: false,
			staleTime: 1000 * 60 * 5,
		},
	},
});

export async function copyToClipboard(text: string) {
	return await navigator.clipboard.writeText(text);
}

export async function copyHtmlToClipboard(html: string) {
	const blob = new Blob([html], { type: 'text/html' });
	const item = new ClipboardItem({ 'text/html': blob });
	return await navigator.clipboard.write([item]);
}

export function getRoute(type: ObjectType.Document, documentId: number, isArchived: boolean): string;
export function getRoute(type: ObjectType.DocumentCategory, categoryId: number, markedDocId?: number): string;
export function getRoute(type: ObjectType, ...args: unknown[]): string {
	switch (type) {
		case ObjectType.Document:
			return args[1] === true ? `/archive/${args[0]}` : `/document-view/${args[0]}`;
		case ObjectType.DocumentCategory:
			if (args.length === 2) {
				return `/document/list/${args[0]}?marked=${args[1]}`;
			}
			return `/document/list/${args[0]}`;
		default:
			throw new Error('Invalid object type');
	}
}

export async function get<T>(endpoint: string) {
	const { headers, url } = getBaseHeadersAndUrl(endpoint);
	const response = await fetch(url, {
		method: 'GET',
		headers,
	});

	return handleResponse<T>(response);
}

export async function post<T>(
	endpoint: string,
	data?: object | number | boolean | string,
	onChunk?: (chunk: string) => void,
	onComplete?: () => void
) {
	const { headers, url } = getBaseHeadersAndUrl(endpoint);
	const { data: postData } = getPostData(data);

	if (postData) {
		headers['Content-Type'] = 'application/json';
		if (typeof postData === 'object') {
			fixDateProps(postData);
		}
	}

	const response = await fetch(url, {
		method: 'POST',
		headers,
		body: postData !== undefined ? JSON.stringify(postData) : undefined,
	});

	if (onChunk) {
		const reader = response.body?.getReader();

		const decoder = new TextDecoder();
		while (true && reader) {
			const { value, done } = await reader.read();
			if (done) break;
			const chunk = decoder.decode(value);
			onChunk(chunk);
		}

		if (onComplete) {
			onComplete();
		}

		return;
	}

	return handleResponse<T>(response);
}

export async function put<T>(endpoint: string, data?: object | number | boolean | string) {
	const { headers, url } = getBaseHeadersAndUrl(endpoint);
	const { data: putData } = getPostData(data);

	if (putData) {
		headers['Content-Type'] = 'application/json';
		if (typeof putData === 'object') {
			fixDateProps(putData);
		}
	}

	const response = await fetch(url, {
		method: 'PUT',
		headers,
		body: putData !== undefined ? JSON.stringify(putData) : undefined,
	});

	return handleResponse<T>(response);
}

export async function postForm<T>(endpoint: string, data: FormData) {
	const { headers, url } = getBaseHeadersAndUrl(endpoint);
	const response = await fetch(url, {
		method: 'POST',
		headers,
		body: data,
	});

	return handleResponse<T>(response);
}

export async function postOnUnload(endpoint: string, data?: object | number | boolean | string): Promise<boolean> {
	const { headers, url } = getBaseHeadersAndUrl(endpoint);
	const { data: postData } = getPostData(data);

	if (postData) {
		headers['Content-Type'] = 'application/json';
		if (typeof postData === 'object') {
			fixDateProps(postData);
		}
	}

	const requestBody = postData !== undefined ? JSON.stringify(postData) : undefined;

	if (navigator.sendBeacon && requestBody !== undefined) {
		const beaconData = new Blob([requestBody], {
			type: headers['Content-Type'] || 'application/json',
		});

		if (navigator.sendBeacon(url, beaconData)) {
			return true;
		}
	}

	try {
		await fetch(url, {
			method: 'POST',
			headers,
			body: requestBody,
			keepalive: true,
		});

		return true;
	} catch {
		return false;
	}
}

export async function upload<T>(endpoint: string, file: File, fields?: Record<string, string>) {
	const { headers, url } = getBaseHeadersAndUrl(endpoint);

	const formData = new FormData();
	formData.append('file', file);
	if (fields) {
		for (const [key, value] of Object.entries(fields)) {
			formData.append(key, value);
		}
	}

	const response = await fetch(url, {
		method: 'POST',
		headers,
		body: formData,
	});

	return handleResponse<T>(response);
}

export async function httpDelete<T>(endpoint: string) {
	const { headers, url } = getBaseHeadersAndUrl(endpoint);
	const response = await fetch(url, {
		method: 'DELETE',
		headers,
	});

	return handleResponse<T>(response);
}

async function handleResponse<T>(response: Response) {
	if (!response.ok) {
		const errorBody = await response.text();
		throw new HttpError(response.status, response.statusText, errorBody);
	}

	const contentType = response.headers.get('Content-Type');
	if (contentType && contentType.includes('application/json')) {
		const result: T = await response.json();
		return result;
	}

	//if content type is image or other blob type
	if (
		contentType &&
		(contentType.startsWith('image/') ||
			contentType.includes('application/pdf') ||
			contentType.includes('application/octet-stream') ||
			contentType.includes('application/ms-excel') ||
			contentType.includes('application/vnd.ms-excel') ||
			contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
			contentType.includes('application/zip'))
	) {
		const blob = await response.blob();
		return blob as unknown as T;
	}

	return null;
}

function getBaseHeadersAndUrl(endpoint: string) {
	const getTrimmedEndpoint = () => {
		let trimmed = endpoint;
		if (trimmed.startsWith('~')) {
			trimmed = trimmed.substring(1);
		}
		if (trimmed.startsWith('/')) {
			return trimmed.substring(1);
		}
		return trimmed;
	};

	const token = getToken();
	const url = getSiteRootPath() + getTrimmedEndpoint();

	const headers: Record<string, string> = {
		'Biz-Origin': 'bizreact',
	};
	if (token) {
		headers['Auth-Token'] = token;
	}

	return { headers, url };
}

function getPostData(data?: object | number | boolean | string): { data?: object | number | boolean | string; isJson: boolean } {
	if (!data) {
		return { data: undefined, isJson: false };
	}
	if (typeof data === 'number' || typeof data === 'boolean' || typeof data === 'string') {
		return { data, isJson: false };
	} else if (data instanceof Array) {
		return { data: [...data], isJson: true };
	} else if (data) {
		return { data: { ...data }, isJson: true };
	}
	return { data: {}, isJson: false };
}

function fixDateProps<T extends object>(o: T): T {
	//todo: activate when needed, need to move date functions first to new frontend
	// Define an object that can be indexed with a string and holds any value
	// const objWithIndexSignature: { [key: string]: any } = o;

	// for (const p in objWithIndexSignature) {
	// 	if (objWithIndexSignature[p] instanceof Date) {
	// 		// Convert Date to string
	// 		objWithIndexSignature[p] = formatToSimpleDateTimeString(objWithIndexSignature[p]);
	// 	} else if (typeof objWithIndexSignature[p] === 'object' && objWithIndexSignature[p] !== null) {
	// 		// Recursive call for nested objects
	// 		fixDateProps(objWithIndexSignature[p]);
	// 	}
	// }

	return o;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getQSFromObject<T extends Record<string, any>>(params: T): URLSearchParams {
	const searchParams = new URLSearchParams();

	Object.entries(params).forEach(([key, value]) => {
		if (value === undefined) return;

		if (Array.isArray(value)) {
			value.forEach((item) => searchParams.append(key, String(item)));
		} else if (value instanceof Date) {
			searchParams.append(key, value.toISOString());
		} else {
			searchParams.append(key, String(value));
		}
	});

	return searchParams;
}

export function hardRedirect(relativePath: string) {
	location.href = getSiteRootPath() + relativePath;
}

export function reloadPage() {
	location.href = getSiteRootPath();
}

export function getToken() {
	const token = localStorage.getItem('BizPartAuthToken');
	if (token) return token;

	return sessionStorage.getItem('BizPartAuthToken');
}

// export function deleteCookie(name: string) {
// 	document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
// }

export function removeToken() {
	sessionStorage.removeItem('BizPartAuthToken');
	localStorage.removeItem('BizPartAuthToken');
	//deleteCookie('BizPartAuthToken');
}

export function setToken(token: string, userId?: number) {
	removeToken();

	if (userId && userId > 0) {
		localStorage.setItem('BizPartAuthToken', token);
	} else {
		sessionStorage.setItem('BizPartAuthToken', token);
	}
}

function appendSizeToFileName(path: string, size: string = ''): string {
	if (!size || size.length <= 0) return path;

	const lowerCasePath = path.toLowerCase();
	const excludedFileTypes = ['.svg', '.gif', '.webp'];
	for (const ext of excludedFileTypes) {
		if (lowerCasePath.endsWith(ext)) {
			return path; // Return original path if it ends with an excluded file type
		}
	}

	if (lowerCasePath.indexOf('getfilehandler.ashx') >= 0 && !lowerCasePath.includes('size=' + size)) {
		return `${path}&size=${size}`;
	} else if (lowerCasePath.indexOf('mediabank/') > -1 && !path.includes('_' + size)) {
		return path.replace(/(\.[^.]+)$/, `_${size}$1`);
	}

	return path;
}

export function getPathWithRootPath(path: string, appendSize?: MediabankImageSize): string {
	if (!path) return path;
	if (path.toLowerCase().startsWith('http')) return path;

	path = path.replace('##UPLOAD##/', getPortalUploadPath());

	let overrideDomain =
		window.location.host === 'localhost' || window.location.host.includes('local.') ? window.portalBaseUrl : '';
	const portalAlias = getPortalAlias();
	const localStorageOverride = getLocalStorage(`override_siteroot_${portalAlias}`.toLowerCase());
	if (localStorageOverride && localStorageOverride.length > 0) overrideDomain = localStorageOverride;
	//let cdnURL = getPortalSetting('CdnURL');

	if (path.toLowerCase().indexOf('getfilehandler.ashx') > -1 || path.toLowerCase().indexOf('mediabank/') > -1) {
		path = appendSizeToFileName(path, appendSize);
	}

	if (portalAlias.length > 0 && (overrideDomain?.length ?? 0) > 0) {
		if (path.toLowerCase().indexOf('getfilehandler.ashx') >= 0) {
			return `${overrideDomain}${path.replace('~/', '')}`;
		} else if (path.toLowerCase().indexOf('upload/') > -1) {
			const normalizedPath = path.replace(/^(.*?Upload\/)/i, '/');
			if (overrideDomain.endsWith('/')) {
				overrideDomain = overrideDomain.slice(0, -1);
			}
			return `${overrideDomain}/Upload${normalizedPath}`;
		}
	}

	let trimmedPath = path;
	if (trimmedPath.startsWith('~')) {
		trimmedPath = trimmedPath.substring(1);
	}
	if (trimmedPath.startsWith('/')) {
		trimmedPath = trimmedPath.substring(1);
	}
	return getSiteRootPath() + trimmedPath;
}

export function getSiteRootPath() {
	//globalProperties is not working in old frontend/admin, so we need this as fallback :( (for now)
	return globalProperties.siteRootPath ?? window.siteRootPath;
}

export function getPortalBaseUrl() {
	return globalProperties.portalBaseUrl ?? window.portalBaseUrl;
}

export function getPortalUploadPath() {
	return globalProperties.portalUploadPath ?? window.portalUploadPath;
}

export function getCultureCode() {
	return globalProperties.cultureCode ?? window.cultureCode;
}

export function getCultureId() {
	return globalProperties.currentCultureId ?? window.currentCultureId;
}

export function getPortalAlias() {
	return globalProperties.portalAlias ?? window.portalAlias;
}

export function getPortalId() {
	return globalProperties.portalId ?? window.portalId;
}

export function getPortalVersion() {
	return globalProperties.portalVersion ?? window.portalVersion;
}

export function getLocalStorage(key: string) {
	const settingKey = `${key}_${getPortalId()}`;
	return localStorage.getItem(settingKey);
}

export function getLocalStorageAsParsedJson<T>(key: string) {
	const value = getLocalStorage(key);
	if (!value) return null;

	try {
		return JSON.parse(value) as T;
	} catch (e) {
		console.error(`Failed to parse localStorage value for key "${key}":`, e);
	}
}

export function setLocalStorage(key: string, value: string) {
	const settingKey = `${key}_${getPortalId()}`;
	localStorage.setItem(settingKey, value);
}

export function removeLocalStorage(key: string) {
	const settingKey = `${key}_${getPortalId()}`;
	localStorage.removeItem(settingKey);
}

export function getTransformedRootPath() {
	const mode = import.meta.env.MODE;
	if (mode === 'dev-live-server') {
		return 'http://localhost:3000/src/';
	}

	const normalRootPath = getSiteRootPath();
	return `${normalRootPath}dist/react/`;
}

export function getTransformedPath(path: string) {
	const rootPath = getTransformedRootPath();
	if (!path) return rootPath;

	return `${rootPath}${path.startsWith('/') ? path.substring(1) : path}`;
}

export class HttpError extends Error {
	status: number;
	statusText: string;
	body: string;

	constructor(status: number, statusText: string, body: string) {
		super(`HTTP ${status}: ${statusText}`);
		this.name = 'HttpError';
		this.status = status;
		this.statusText = statusText;
		this.body = body;
		// Maintains proper stack trace for where our error was thrown (only available on V8)
		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, HttpError);
		}
	}
}
